import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { cx, isFilled } from '@/shared/lib'
import s from './SectionCaption.module.css'

/** Уровень заголовка. Не задан — подпись остаётся `div` и в план заголовков не попадает. */
export type SectionCaptionLevel = 2 | 3 | 4 | 5 | 6

export type SectionCaptionProps = {
  /** Текст подписи **в обычном регистре**: заглавные ставит CSS, а не JSX и не локаль. */
  children: ReactNode
  /**
   * `h2`…`h6` для заголовка блока. Для подписи группы в меню уровень не задаётся:
   * `heading` не входит в список допустимых потомков `role="menu"`.
   */
  level?: SectionCaptionLevel
  /** `id` узла с текстом: по нему группа или секция получает имя через `aria-labelledby`. */
  labelId?: string
  /**
   * Слот в конце строки: счётчик, ссылка-действие, значение, сводка,
   * `<Divider grow decorative tone="subtle" />` вместо линии-заполнителя.
   */
  aside?: ReactNode
  /** Корень строки. */
  ref?: Ref<HTMLDivElement>
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'>

// Карта объявлена вне рендера: тег не создаётся заново на каждую отрисовку
const CAPTION_TAG: Record<SectionCaptionLevel, 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> = {
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
}

/**
 * Капслок-подпись раздела: называет блок содержимого внутри плотной панели.
 * Регистр делает `text-transform`, семантику выбирает потребитель через `level`.
 * Контракт: docs/ui-kit/contracts/section-caption.md
 */
export function SectionCaption({
  children,
  level,
  labelId,
  aside,
  className,
  ref,
  ...rest
}: SectionCaptionProps) {
  const Caption = level === undefined ? 'div' : CAPTION_TAG[level]
  // aside={can('edit') && <Link…>} приходит булевым: пустая коробка ломала бы выравнивание
  const hasAside = isFilled(aside)

  return (
    <div {...rest} ref={ref} className={cx(s.root, className)} data-level={level ?? 'none'}>
      <Caption id={labelId} className={s.caption}>
        {children}
      </Caption>
      {hasAside ? <div className={s.aside}>{aside}</div> : null}
    </div>
  )
}
